
import React, { useState } from "react";
import { Edit, BookText, MessageSquare, Tag, CalendarIcon, Clock, HelpCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SermonFormData } from "@/hooks/sermon-plans/types";
import { useForm } from "react-hook-form";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface ManualTabContentProps {
  setFormData: (data: SermonFormData) => void;
}

export function ManualTabContent({ setFormData }: ManualTabContentProps) {
  const [date, setDate] = useState<Date | undefined>(undefined);
  
  const form = useForm<SermonFormData>({
    defaultValues: {
      title: "",
      scripture_reference: "",
      notes: "",
      planned_date: "",
      target_duration: "30",
      sermon_type: "Expository",
      congregation_id: ""
    }
  });

  // Update parent component's form data when our form changes
  React.useEffect(() => {
    const subscription = form.watch((value) => {
      setFormData(value as SermonFormData);
    });
    return () => subscription.unsubscribe();
  }, [form, setFormData]);

  // Update the planned_date in the form when the date picker value changes
  React.useEffect(() => {
    if (date) {
      form.setValue('planned_date', format(date, 'yyyy-MM-dd'));
    } else {
      form.setValue('planned_date', '');
    }
  }, [date, form]);

  return (
    <div className="space-y-4">
      <div className="flex items-start">
        <Edit className="h-5 w-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
        <div>
          <h3 className="font-medium">Create your sermon from scratch</h3>
          <p className="text-gray-500 text-sm mt-1">
            Start with a blank canvas and create your sermon plan using your own ideas and inspiration.
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        <div className="border rounded-md p-3 bg-blue-50 border-blue-100">
          <div className="flex items-center mb-2">
            <BookText className="h-4 w-4 text-blue-600 mr-2" />
            <h4 className="text-sm font-medium text-blue-800">Essential Details</h4>
          </div>
          <p className="text-xs text-blue-700">
            Add title, scripture reference, and planned date
          </p>
        </div>
        
        <div className="border rounded-md p-3 bg-blue-50 border-blue-100">
          <div className="flex items-center mb-2">
            <MessageSquare className="h-4 w-4 text-blue-600 mr-2" />
            <h4 className="text-sm font-medium text-blue-800">Sermon Notes</h4>
          </div>
          <p className="text-xs text-blue-700">
            Capture your thoughts, outline, and key points for the message
          </p>
        </div>
      </div>

      <div className="border rounded-md p-4 bg-gray-50 mt-4">
        <h4 className="text-sm font-medium mb-3 flex items-center">
          <BookText className="h-4 w-4 mr-2 text-gray-700" />
          Sermon Form
        </h4>
        
        <Form {...form}>
          <div className="space-y-3">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-medium text-gray-700">Sermon Title</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter sermon title" 
                      className="h-8 text-sm"
                      {...field} 
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="scripture_reference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-medium text-gray-700 flex items-center">
                      <Tag className="h-3 w-3 mr-1" />Scripture Reference
                    </FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="e.g. John 3:16" 
                        className="h-8 text-sm"
                        {...field} 
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormItem>
                <FormLabel className="text-xs font-medium text-gray-700 flex items-center">
                  <CalendarIcon className="h-3 w-3 mr-1" />Planned Date
                </FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal h-8 text-sm",
                        !date && "text-muted-foreground"
                      )}
                    >
                      {date ? format(date, "PPP") : <span>Select date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </FormItem>
            </div>
            
            <FormField
              control={form.control}
              name="target_duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-medium text-gray-700 flex items-center">
                    <Clock className="h-3 w-3 mr-1" />Target Duration
                  </FormLabel>
                  <FormControl>
                    <Input 
                      className="h-8 text-sm"
                      {...field} 
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-medium text-gray-700 flex items-center">
                    <MessageSquare className="h-3 w-3 mr-1" />Sermon Notes
                  </FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter sermon notes, outline, key points..." 
                      className="min-h-[100px] text-sm"
                      {...field} 
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </Form>
      </div>
      
      <div className="flex items-center p-3 bg-amber-50 border border-amber-100 rounded-md">
        <div className="bg-amber-100 p-2 rounded-full mr-3">
          <HelpCircle className="h-4 w-4 text-amber-600" />
        </div>
        <p className="text-xs text-amber-800">
          Click "Create Manually" below to create your sermon plan with the details you've entered.
        </p>
      </div>
    </div>
  );
}
